import React, { useState } from 'react';
import type { RfmBadgeSize, RfmTag, RfmTagBadgeProps } from './types';

// ─── Segment config ───────────────────────────────────────────────────────────

interface SegmentConfig {
  label: string;
  description: string;
  bg: string;
  text: string;
  border: string;
  icon: React.ReactElement;
}

const CROWN_ICON = (
  <svg viewBox="0 0 12 12" fill="none" aria-hidden="true" className="shrink-0">
    <path d="M1 9h10M2 9L1 4l3 2.5L6 2l2 4.5L11 4l-1 5H2z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
  </svg>
);

const HEART_ICON = (
  <svg viewBox="0 0 12 12" fill="currentColor" aria-hidden="true" className="shrink-0">
    <path d="M6 10.5S1 7 1 4a2.5 2.5 0 015 0 2.5 2.5 0 015 0c0 3-5 6.5-5 6.5z" />
  </svg>
);

const TRENDING_ICON = (
  <svg viewBox="0 0 12 12" fill="none" aria-hidden="true" className="shrink-0">
    <path d="M1 9l3.5-3.5L7 8l4-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M8 3h3v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ALERT_ICON = (
  <svg viewBox="0 0 12 12" fill="none" aria-hidden="true" className="shrink-0">
    <path d="M6 1L11 10H1L6 1z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
    <path d="M6 5v2M6 8.5v.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

const MOON_ICON = (
  <svg viewBox="0 0 12 12" fill="none" aria-hidden="true" className="shrink-0">
    <path d="M10 7A4 4 0 015 2a5 5 0 100 8 4 4 0 005-3z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

const SEGMENT_CONFIG: Record<RfmTag, SegmentConfig> = {
  CHAMPION: {
    label: 'Champion',
    description: 'High spend, frequent, recent visitor',
    bg: 'bg-success-50',
    text: 'text-success-800',
    border: 'border-success-300',
    icon: CROWN_ICON,
  },
  LOYAL: {
    label: 'Loyal',
    description: 'Regular buyer with strong frequency',
    bg: 'bg-brand-50',
    text: 'text-brand-800',
    border: 'border-brand-300',
    icon: HEART_ICON,
  },
  POTENTIAL_LOYALIST: {
    label: 'Potential',
    description: 'Recent customer with growing engagement',
    bg: 'bg-info-50',
    text: 'text-info-800',
    border: 'border-info-300',
    icon: TRENDING_ICON,
  },
  AT_RISK: {
    label: 'At Risk',
    description: 'Was a good customer but has not visited recently',
    bg: 'bg-warning-50',
    text: 'text-warning-800',
    border: 'border-warning-300',
    icon: ALERT_ICON,
  },
  INACTIVE: {
    label: 'Inactive',
    description: 'No visits or purchases in a long time',
    bg: 'bg-neutral-100',
    text: 'text-neutral-600',
    border: 'border-neutral-300',
    icon: MOON_ICON,
  },
};

const SIZE_CLASSES: Record<RfmBadgeSize, { badge: string; icon: string }> = {
  sm: { badge: 'h-5 px-2 text-xs gap-1',    icon: 'w-3 h-3' },
  md: { badge: 'h-6 px-2.5 text-xs gap-1.5', icon: 'w-3.5 h-3.5' },
};

// ─── RfmTagBadge ──────────────────────────────────────────────────────────────

export function RfmTagBadge({
  tag,
  size = 'md',
  showTooltip = true,
  className,
}: RfmTagBadgeProps) {
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const config = SEGMENT_CONFIG[tag];
  const sizes = SIZE_CLASSES[size];

  const badgeClasses = [
    'relative inline-flex items-center font-medium rounded-full whitespace-nowrap border',
    sizes.badge,
    config.bg,
    config.text,
    config.border,
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  const iconEl = React.cloneElement(config.icon, { className: sizes.icon });

  return (
    <span
      role="status"
      aria-label={`RFM segment: ${config.label} — ${config.description}`}
      className={badgeClasses}
      onMouseEnter={() => showTooltip && setTooltipVisible(true)}
      onMouseLeave={() => setTooltipVisible(false)}
      onFocus={() => showTooltip && setTooltipVisible(true)}
      onBlur={() => setTooltipVisible(false)}
      tabIndex={showTooltip ? 0 : undefined}
    >
      {iconEl}
      {config.label}

      {showTooltip && tooltipVisible && (
        <span
          role="tooltip"
          className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 z-10 whitespace-nowrap rounded-md bg-neutral-900 px-2.5 py-1 text-xs font-normal text-white shadow-md pointer-events-none"
        >
          {config.description}
          <span
            className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-neutral-900"
            aria-hidden="true"
          />
        </span>
      )}
    </span>
  );
}
